/**
 * Mock creator data + simulated subscription/tip records.
 *
 * Used as fallback demo content on /dashboard/creators (when the API
 * returns no subscriptions yet) and as the source of truth for the
 * Creators directory section embedded in /feed.
 *
 * When the API ships real creator + subscription endpoints, the
 * shapes here mirror SubscriptionRecord / TipRecord so swap-in is a
 * one-import change in the consumer.
 */

import type {
  SubscriptionRecord,
  TipRecord,
  SubscriptionState,
} from "@/lib/api";

export interface MockCreator {
  username: string;
  displayName: string;
  bio: string;
  category: string;
  monthlyPriceCents: number;
  followers: number;
  postsThisMonth: number;
  tier: "Free" | "Premium" | "VIP";
  region: string; // emoji flag
  verified?: boolean;
}

export const MOCK_CREATORS: MockCreator[] = [
  {
    username: "marketwatch",
    displayName: "MarketWatch Daily",
    bio: "Daily market analysis on politics, crypto, and economics. 4 takes per day.",
    category: "Markets analysis",
    monthlyPriceCents: 500,
    followers: 12_400,
    postsThisMonth: 96,
    tier: "Premium",
    region: "🇺🇸",
    verified: true,
  },
  {
    username: "crypto_skeptic",
    displayName: "Crypto Skeptic",
    bio: "Honest takes on crypto markets — no shilling, no charts that lie.",
    category: "Crypto",
    monthlyPriceCents: 1500,
    followers: 8_200,
    postsThisMonth: 32,
    tier: "VIP",
    region: "🇨🇦",
    verified: true,
  },
  {
    username: "lula_2026",
    displayName: "Lula 2026 watch",
    bio: "Brazilian politics coverage. Polling data + scenario calls.",
    category: "Politics",
    monthlyPriceCents: 0,
    followers: 5_400,
    postsThisMonth: 18,
    tier: "Free",
    region: "🇧🇷",
  },
  {
    username: "tia_da_bolsa",
    displayName: "Tia da Bolsa",
    bio: "Mercados brasileiros explicados em português claro. Daily macro + commodities.",
    category: "Brazilian markets",
    monthlyPriceCents: 800,
    followers: 9_100,
    postsThisMonth: 41,
    tier: "Premium",
    region: "🇧🇷",
    verified: true,
  },
  {
    username: "uefa_ops",
    displayName: "UEFA Ops",
    bio: "Champions League + Euro analysis. Squad news, lineups, injury intel.",
    category: "Sports",
    monthlyPriceCents: 1000,
    followers: 6_700,
    postsThisMonth: 28,
    tier: "Premium",
    region: "🇪🇸",
  },
  {
    username: "fed_watch",
    displayName: "Fed Watch",
    bio: "FOMC calls, rate-cut probabilities, dot plot dissection.",
    category: "Economics",
    monthlyPriceCents: 1200,
    followers: 14_800,
    postsThisMonth: 22,
    tier: "Premium",
    region: "🇺🇸",
    verified: true,
  },
  {
    username: "ai_skeptic",
    displayName: "AI Skeptic",
    bio: "Honest takes on AI hype + capabilities. GPT release calls, regulation watch.",
    category: "Tech",
    monthlyPriceCents: 700,
    followers: 5_900,
    postsThisMonth: 36,
    tier: "Premium",
    region: "🇬🇧",
  },
  {
    username: "world_cup_jose",
    displayName: "World Cup José",
    bio: "Free updates on the 2026 World Cup. Bracket calls + qualifier analysis.",
    category: "Sports",
    monthlyPriceCents: 0,
    followers: 3_200,
    postsThisMonth: 14,
    tier: "Free",
    region: "🇲🇽",
  },
];

/**
 * Build mock SubscriptionRecord rows for /dashboard/creators fallback.
 * Uses the same shape as the real API so the rendering code doesn't
 * branch on data origin.
 */
export function mockSubscriptionRecords(): SubscriptionRecord[] {
  const now = Date.now();
  return [
    {
      id: "sub-mock-1",
      subscriberId: "demo-user",
      creatorId: "demo-marketwatch",
      creatorUsername: "marketwatch",
      monthlyPriceCents: "500",
      state: "active" as SubscriptionState,
      startedAt: new Date(now - 90 * 86_400_000).toISOString(),
      currentPeriodStartedAt: new Date(now - 18 * 86_400_000).toISOString(),
      currentPeriodExpiresAt: new Date(now + 12 * 86_400_000).toISOString(),
      cancelledAt: null,
      lastRenewalAt: new Date(now - 18 * 86_400_000).toISOString(),
      createdAt: new Date(now - 90 * 86_400_000).toISOString(),
      updatedAt: new Date(now - 18 * 86_400_000).toISOString(),
    },
    {
      id: "sub-mock-2",
      subscriberId: "demo-user",
      creatorId: "demo-crypto-skeptic",
      creatorUsername: "crypto_skeptic",
      monthlyPriceCents: "1500",
      state: "active" as SubscriptionState,
      startedAt: new Date(now - 60 * 86_400_000).toISOString(),
      currentPeriodStartedAt: new Date(now - 25 * 86_400_000).toISOString(),
      currentPeriodExpiresAt: new Date(now + 5 * 86_400_000).toISOString(),
      cancelledAt: null,
      lastRenewalAt: new Date(now - 25 * 86_400_000).toISOString(),
      createdAt: new Date(now - 60 * 86_400_000).toISOString(),
      updatedAt: new Date(now - 25 * 86_400_000).toISOString(),
    },
    {
      id: "sub-mock-3",
      subscriberId: "demo-user",
      creatorId: "demo-tia-bolsa",
      creatorUsername: "tia_da_bolsa",
      monthlyPriceCents: "800",
      state: "cancelled" as SubscriptionState,
      startedAt: new Date(now - 200 * 86_400_000).toISOString(),
      currentPeriodStartedAt: new Date(now - 45 * 86_400_000).toISOString(),
      currentPeriodExpiresAt: new Date(now - 15 * 86_400_000).toISOString(),
      cancelledAt: new Date(now - 22 * 86_400_000).toISOString(),
      lastRenewalAt: new Date(now - 75 * 86_400_000).toISOString(),
      createdAt: new Date(now - 200 * 86_400_000).toISOString(),
      updatedAt: new Date(now - 22 * 86_400_000).toISOString(),
    },
  ];
}

/**
 * Mock tips — populated on both directions (sent + received) so the
 * /dashboard/creators tips section has content even in a fresh dev
 * environment.
 */
export function mockTipsSent(): TipRecord[] {
  const now = Date.now();
  return [
    {
      id: "tip-mock-s1",
      tipperId: "demo-user",
      recipientId: "demo-marketwatch",
      recipientUsername: "marketwatch",
      amountCents: "200",
      note: "Great FOMC breakdown",
      ledgerTxId: "ledger-mock-1",
      sourcePostId: null,
      createdAt: new Date(now - 2 * 86_400_000).toISOString(),
    },
    {
      id: "tip-mock-s2",
      tipperId: "demo-user",
      recipientId: "demo-fed-watch",
      recipientUsername: "fed_watch",
      amountCents: "500",
      note: null,
      ledgerTxId: "ledger-mock-2",
      sourcePostId: null,
      createdAt: new Date(now - 9 * 86_400_000).toISOString(),
    },
  ];
}

export function mockTipsReceived(): TipRecord[] {
  const now = Date.now();
  return [
    {
      id: "tip-mock-r1",
      tipperId: "demo-whale-alpha",
      recipientId: "demo-user",
      recipientUsername: "whale_alpha",
      amountCents: "300",
      note: "Solid Brazil 2026 call",
      ledgerTxId: "ledger-mock-3",
      sourcePostId: null,
      createdAt: new Date(now - 4 * 86_400_000).toISOString(),
    },
  ];
}
